import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import * as Yup from "yup";

import { FormControl, FormErrorMessage, FormLabel, Tooltip } from "@chakra-ui/react";
import { ENTITY } from "@equidefi/constants/profile";
import { ASCII_ONLY_MESSAGE, ASCII_ONLY_REGEX } from "@equidefi/constants/validation";
import { BannerAlert, Input, Modal, PhoneField, RadioGroup } from "@equidefi/ui";
import { useCurrentUser } from "../hooks/useUsers";

const userSchema = {
  first_name: Yup.string()
    .nullable()
    .matches(ASCII_ONLY_REGEX, ASCII_ONLY_MESSAGE)
    .required("Please enter your first name"),
  middle_name: Yup.string().nullable().matches(ASCII_ONLY_REGEX, ASCII_ONLY_MESSAGE),
  last_name: Yup.string()
    .nullable()
    .matches(ASCII_ONLY_REGEX, ASCII_ONLY_MESSAGE)
    .required("Please enter your last name"),
  phone_number: Yup.string().test({
    name: "phone_number",
    message: "Must be a valid phone number",
    test: (value) => (value ? isValidPhoneNumber(value) : false),
  }),
  email: Yup.string().nullable().email("Must be a valid email").required("Please enter your email address"),
};

const entitySchema = {
  is_kyc: Yup.boolean().test({
    message: "Required",
    test: (value) => typeof value === "boolean",
  }),
  is_signer: Yup.boolean()
    .test({
      message: "Required",
      test: (value) => typeof value === "boolean",
    })
    .when(["is_kyc"], (isKYC, schema) => {
      if (isKYC === false) {
        return schema.test({
          name: "test_is_signer",
          message: "Added person must be either a UBO or a signer.",
          test: (value) => value === true,
        });
      }
      return schema;
    }),
};

const buildSchema = (entityType) => {
  if (entityType === ENTITY) {
    return Yup.object().shape({
      ...userSchema,
      ...entitySchema,
    });
  }

  return Yup.object().shape(userSchema);
};

export const SignerModal = ({
  initialValues: data = {},
  isOpen = false,
  onHide,
  onSubmit,
  onDelete,
  investment,
  canAddSigner,
  canAddUBO,
}) => {
  const { data: user } = useCurrentUser();

  const isUBODisabled = !canAddUBO && !data?.is_kyc;
  const isSignerDisabled = !canAddSigner && !data?.is_signer;

  const initialValues = {
    first_name: data?.first_name || "",
    middle_name: data?.middle_name || "",
    last_name: data?.last_name || "",
    phone_number: data?.phone_number || "",
    email: data?.email || "",
    order: data?.order,
    is_kyc: isUBODisabled ? false : data?.is_kyc,
    is_signer: isSignerDisabled ? false : data?.is_signer,
  };

  const {
    register,
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    reset,
    formState: { errors, touchedFields, isValid, isSubmitting },
  } = useForm({
    values: initialValues,
    resolver: yupResolver(buildSchema(investment?.vault?.entity_type)),
    mode: "onTouched",
  });

  if (!isOpen) {
    return null;
  }

  const isEdit = !!initialValues?.email;

  const buttons = [
    {
      type: "submit",
      label: isEdit ? "Save Changes" : "Add Individual",
      // isSubmitting guards against a double-click submitting the signer twice
      isDisabled: !isValid || isSubmitting,
    },
    ...(isEdit
      ? [
          {
            action: () => {
              onDelete(data.id);
              onHide();
            },
            colorScheme: "red",
            label: "Delete",
          },
        ]
      : []),
    { action: onHide, variant: "outline", label: "Dismiss" },
  ];

  return (
    <Modal
      contentProps={{ w: { base: "full", sm: "640" } }}
      isOpen
      title={isEdit ? "Edit Individual" : "Add Another Individual"}
      onClose={() => {
        reset();
        onHide();
      }}
      onSubmit={handleSubmit((values) => onSubmit(values))}
      buttons={buttons}>
      <Input
        label="First Name"
        isRequired
        error={errors.first_name?.message}
        isInvalid={!!errors.first_name}
        {...register("first_name")}
      />
      <Input
        label="Middle Name"
        error={errors.middle_name?.message}
        isInvalid={!!errors.middle_name}
        {...register("middle_name")}
      />
      <Input
        label="Last Name"
        isRequired
        error={errors.last_name?.message}
        isInvalid={!!errors.last_name}
        {...register("last_name")}
      />
      {isEdit && (
        <Tooltip
          placement="auto-start"
          label="To edit this email address, you must first fully remove this signer by clicking the delete button. Once removed, click Add Additional Signer to add the signer again including the new email address."
          hasArrow>
          <Input
            label="Email"
            type="email"
            isDisabled={initialValues?.email === user?.email || isEdit}
            isRequired
            error={errors.email?.message}
            autoCapitalize="none"
            isInvalid={!!errors.email}
            {...register("email")}
          />
        </Tooltip>
      )}
      {!isEdit && (
        <Input
          label="Email"
          type="email"
          isDisabled={initialValues?.email === user?.email || isEdit}
          isRequired
          error={errors.email?.message}
          autoCapitalize="none"
          isInvalid={!!errors.email}
          {...register("email")}
        />
      )}

      <Controller
        control={control}
        name="phone_number"
        render={({ field: { value, onChange, onBlur } }) => (
          <PhoneField
            name="phone_number"
            error={errors.phone_number?.message}
            isInvalid={!!errors.phone_number}
            value={value}
            // The input emits undefined when cleared, which makes Controller fall back to the pre-filled value
            onChange={(v) => onChange(v ?? "")}
            onBlur={onBlur}
            isRequired
            label="Mobile Phone"
          />
        )}
      />

      {investment?.vault?.entity_type === ENTITY && (
        <>
          <FormControl
            isRequired
            mt="3"
            isInvalid={(touchedFields.is_signer || touchedFields.is_kyc) && !!errors.is_kyc}>
            <FormLabel p="0" mt="0" mb="0.5rem" fontSize="md">
              Is this individual a beneficial owner?
            </FormLabel>
            <RadioGroup
              name="is_kyc"
              value={watch("is_kyc")}
              direction="horizontal"
              isDisabled={isUBODisabled}
              onChange={(value) => {
                setValue("is_kyc", value, { shouldTouch: true, shouldDirty: true });
                trigger(["is_kyc", "is_signer"]);
              }}
              options={[
                {
                  value: true,
                  label: "Yes",
                },
                {
                  value: false,
                  label: "No",
                },
              ]}
            />
            <FormErrorMessage>{errors.is_kyc?.message}</FormErrorMessage>
            {isUBODisabled && (
              <BannerAlert
                mb="4"
                status="error"
                description="Maximum number of UBOs have been added. Please remove or edit current UBOs to make changes."
              />
            )}
          </FormControl>
          <FormControl isRequired isInvalid={(touchedFields.is_signer || touchedFields.is_kyc) && !!errors.is_signer}>
            <FormLabel p="0" mt="0" mb="0.5rem" fontSize="md">
              Is this individual required to sign agreements?
            </FormLabel>
            <RadioGroup
              name="is_signer"
              value={watch("is_signer")}
              direction="horizontal"
              isDisabled={isSignerDisabled}
              onChange={(value) => {
                setValue("is_signer", value, { shouldTouch: true, shouldDirty: true });
                trigger(["is_kyc", "is_signer"]);
              }}
              options={[
                {
                  value: true,
                  label: "Yes",
                },
                {
                  value: false,
                  label: "No",
                },
              ]}
            />
            {isSignerDisabled && (
              <BannerAlert
                status="error"
                description="Maximum number of Signers have been added. Please remove or edit current Signers to make changes."
              />
            )}
            <FormErrorMessage>{errors.is_signer?.message}</FormErrorMessage>
          </FormControl>
        </>
      )}
    </Modal>
  );
};
